// API Configuration
// This allows the app to work on mobile devices, Docker, and different environments

import { ENV } from '@/modules/config/environment';
import { AuthLogic, AuthService, JWTLogic } from '@/modules/iam';
import { DEVICE_ID_HEADER, ensureDeviceId } from '@/util/deviceId';

// Extended interface for PWA auth service with additional methods
interface PWAAuthService extends AuthService {
  handleAuthFailure?(): Promise<void>;
}

export const API_BASE_URL = ENV.apiBaseUrl;

// Export function to get API URL (used by casbin service)
export const getApiUrl = () => API_BASE_URL;

/**
 * Creates a full endpoint URL for third-party libraries.
 * In development, ENV.apiBaseUrl is empty (for Vite proxy), so we use relative URLs.
 * In other environments, ENV.apiBaseUrl already has the correct full URL.
 */
export const createEndpointUrl = (endpoint: string): string =>
  `${getApiUrl()}${endpoint}`;

/**
 * True when an error thrown from `apiCall`/`fetch` is a transport-level failure
 * — the request never produced an HTTP response — rather than an application
 * error surfaced from a non-ok response. The browser/engine reports these as a
 * TypeError whose message varies: "Failed to fetch" (Chrome), "Load failed"
 * (Safari / iOS WKWebView), "NetworkError when attempting to fetch resource"
 * (Firefox), or "The network connection was lost". Callers use this to show an
 * actionable "check your connection" message instead of the raw engine text,
 * and to avoid mistaking a dropped request for, e.g., an invalid code (#386).
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof Error) {
    return /load failed|failed to fetch|networkerror|network connection|network request failed/i.test(
      error.message
    );
  }
  return false;
};

/** User-facing message for a transport-level request failure. */
export const NETWORK_ERROR_MESSAGE =
  'Network error. Please check your connection and try again.';

// Helper function to make API calls
export const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const url = createEndpointUrl(endpoint);
  const method = (options.method || 'GET').toUpperCase();

  // Build headers object
  const deviceId = await ensureDeviceId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // 'ngrok-skip-browser-warning': 'true',
    'X-Auth-Transport': ENV.authTransport,
    [DEVICE_ID_HEADER]: deviceId,
  };

  // Add authentication based on platform
  const authService = AuthLogic.getAuthService();
  const isAuthenticated = await authService.isAuthenticated();

  if (isAuthenticated) {
    // Check if we're using HTTP-only cookies (PWA) or Bearer tokens (Native)
    const token = await authService.getToken();
    if (token && token !== 'http-only-cookie') {
      // Native platform - use Bearer token
      headers['Authorization'] = `Bearer ${token}`;
    } else if (token === 'http-only-cookie') {
      // PWA platform - try Bearer token as fallback if available
      try {
        const bearerToken = await authService.getBearerToken();
        if (bearerToken) {
          headers['Authorization'] = `Bearer ${bearerToken}`;
        }
      } catch {
        // Bearer token not available, use HTTP-only cookies
      }
      // HTTP-only cookies are sent automatically with credentials: 'include'
    }
  }

  // Debug logging (only in development)
  if (ENV.isDevelopment) {
    // eslint-disable-next-line no-console
    console.log('API call:', options.method || 'GET', url);
  }

  // Bearer mode: omit cookies so stale auth_token cookies don't ride along and
  // shadow the Authorization header. Cookie mode: include as before.
  const credentials: RequestCredentials =
    ENV.authTransport === 'bearer' ? 'omit' : 'include';

  const response = await fetch(url, {
    ...options,
    credentials,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  // Handle authentication failures for PWA platform
  if (response.status === 401 && isAuthenticated) {
    const token = await authService.getToken();
    if (token === 'http-only-cookie') {
      // Try to fall back to Bearer token
      try {
        const bearerToken = await authService.getBearerToken();
        if (bearerToken) {
          // Retry the request with Bearer token
          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${bearerToken}`,
          };

          const retryResponse = await fetch(url, {
            ...options,
            credentials,
            headers: {
              ...retryHeaders,
              ...options.headers,
            },
          });

          // If retry succeeds, trigger fallback
          if (retryResponse.ok) {
            await (authService as PWAAuthService).handleAuthFailure?.();
          }

          if (method !== 'GET') {
            await JWTLogic.applyTokenFromResponse(retryResponse, authService);
          }
          return retryResponse;
        }
      } catch {
        // Bearer token not available, return original response
      }
    }
  }

  // Log response status for development (but don't treat expected 404s as errors)
  if (ENV.isDevelopment && !response.ok) {
    if (response.status === 404) {
      // eslint-disable-next-line no-console
      console.log(`API: Resource not found (404) - ${url}`);
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        `API: Request failed with status ${response.status} - ${url}`
      );
    }
  }

  if (method !== 'GET') {
    await JWTLogic.applyTokenFromResponse(response, authService);
  }
  return response;
};
